/**
 * Billing (employer self-manage) — the complete billing home for employers.
 *
 * Sections (top → bottom):
 *   1. Current plan card — name, price, next billing, seats/quota progress bars,
 *      status pill. Two big CTAs: "Change plan" (opens plan-comparison drawer)
 *      and "Cancel subscription" (opens confirm dialog).
 *   2. Outstanding amount + total invoices snapshot.
 *   3. Invoice ledger with per-row Download / Receipt / Pay.
 *
 * Change-plan drawer inlines the /plans catalogue with a one-click checkout
 * that routes through Cashfree for paid tiers or /onboarding-complete for
 * the free plan (downgrade). Cancel calls /subscription/cancel and refreshes.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Download, Loader2, CreditCard, FileText, ArrowUpRight, ArrowDownRight,
    CheckCircle2, Sparkles, XCircle, X, Package,
} from "lucide-react";

const STATUS_STYLE = {
    paid: "border-emerald-500 text-emerald-700 bg-emerald-50",
    pending: "border-amber-500 text-amber-700 bg-amber-50",
    overdue: "border-rose-500 text-rose-700 bg-rose-50",
    cancelled: "border-neutral-400 text-neutral-500 bg-neutral-50",
    draft: "border-blue-300 text-blue-700 bg-blue-50",
};

const SUB_STATUS_STYLE = {
    active: "border-emerald-500 text-emerald-700 bg-emerald-50",
    trialing: "border-blue-500 text-blue-700 bg-blue-50",
    past_due: "border-amber-500 text-amber-700 bg-amber-50",
    grace: "border-amber-500 text-amber-700 bg-amber-50",
    suspended: "border-rose-600 text-rose-700 bg-rose-50",
    cancelled: "border-neutral-400 text-neutral-500 bg-neutral-50",
};

function money(n, currency = "INR") {
    if (n == null) return "—";
    try {
        return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
    } catch { return `${currency} ${n}`; }
}

function ordinalRank(k) {
    // Rank plans by "size" so we can label a transition as upgrade / downgrade
    // without hardcoding directional metadata into the API response.
    return { free: 0, starter: 1, pro: 2, enterprise: 3 }[k] ?? 0;
}

export default function Billing() {
    const [invoices, setInvoices] = useState([]);
    const [status, setStatus] = useState(null);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showChangePlan, setShowChangePlan] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [changing, setChanging] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [inv, st, pl] = await Promise.all([
                api.get("/invoices/mine"),
                api.get("/invoices/subscription/status"),
                api.get("/plans"),
            ]);
            setInvoices(inv.data.items || []);
            setStatus(st.data);
            setPlans(pl.data.plans || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const outstanding = useMemo(() => invoices.filter((i) => i.status !== "paid" && i.status !== "cancelled"), [invoices]);
    const outstandingTotal = outstanding.reduce((a, b) => a + (b.total || 0), 0);
    const currency = invoices[0]?.currency || "INR";
    const currentPlanKey = status?.plan_key || status?.plan || "free";
    const currentPlan = plans.find((p) => p.key === currentPlanKey) || { key: currentPlanKey, name: currentPlanKey, price_inr: 0, features: [] };
    const currentPlanPrice = currentPlan.price_inr || 0;

    function downloadPdf(inv, receipt = false) {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const url = `${backend}/api/invoices/${inv.id}/pdf${receipt ? "?receipt=true" : ""}`;
        window.open(url, "_blank", "noopener");
    }

    async function payInvoice(inv) {
        try {
            const { data } = await api.post("/subscription/checkout", {
                invoice_id: inv.id,
                currency: inv.currency,
                gateway: "cashfree",
            });
            const url = data?.payment_link || data?.checkout_url || data?.hosted_url;
            if (url) { window.location.href = url; return; }
            if (data?.integration === "mock" && data?.order_id) {
                await api.post("/subscription/confirm", { checkout_id: data.order_id });
                toast.success("Invoice marked paid (mock mode)");
                load();
                return;
            }
            toast.error("Checkout URL missing from gateway response");
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function changePlan(planKey) {
        if (planKey === currentPlanKey) return;
        setChanging(planKey);
        try {
            if (planKey === "free") {
                // Downgrade to free — just cancel current paid sub. Backend
                // moves them to the free tier with immediate effect.
                await api.post("/subscription/cancel", { reason: "Downgrade to free plan" });
                toast.success("Downgraded to Free");
                setShowChangePlan(false);
                await load();
                return;
            }
            const { data } = await api.post("/subscription/checkout", { plan_key: planKey, currency: "INR" });
            if (data?.integration === "cashfree" && data?.payment_session_id) {
                const base = data.env === "PROD" ? "https://payments.cashfree.com" : "https://payments-test.cashfree.com";
                window.location.href = `${base}/pgapp/pgv/redirect?sessionId=${data.payment_session_id}`;
                return;
            }
            if (data?.payment_link || data?.checkout_url) {
                window.location.href = data.payment_link || data.checkout_url;
                return;
            }
            if (data?.integration === "mock" && data?.order_id) {
                await api.post("/subscription/confirm", { checkout_id: data.order_id });
                toast.success(`Upgraded to ${planKey}`);
                setShowChangePlan(false);
                await load();
                return;
            }
            toast.error("Checkout could not be started — try again later.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setChanging(null); }
    }

    async function cancelSubscription() {
        setCancelling(true);
        try {
            await api.post("/subscription/cancel", { reason: "User cancelled via billing page" });
            toast.success("Subscription cancelled. You'll retain access until the end of the current period.");
            setShowCancel(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCancelling(false); }
    }

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="billing-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Workspace</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight">Billing</h1>
                <p className="text-xs text-muted-foreground mt-1">Manage your plan, invoices and payment method.</p>
            </header>

            {/* ─── Current plan card ───────────────────────────────── */}
            <section className="mb-5 border border-border bg-white p-5" data-testid="billing-current-plan">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Current plan</div>
                        <div className="flex items-baseline gap-2 mt-1">
                            <span className="font-display font-black text-2xl uppercase text-ikb">{currentPlan.name}</span>
                            <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${SUB_STATUS_STYLE[status?.status || "active"] || "border-border"}`} data-testid="billing-sub-status">
                                {status?.status || "active"}
                            </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                            {currentPlanPrice > 0
                                ? <span><b className="text-foreground font-display text-lg">{money(currentPlanPrice, "INR")}</b>/month</span>
                                : <span>Free forever</span>}
                            {status?.next_billing_at && (
                                <span> · next billing on <b className="text-foreground">{status.next_billing_at.slice(0, 10)}</b></span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => setShowChangePlan(true)}
                            className="h-9 px-3 border border-ikb bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5"
                            data-testid="billing-change-plan"
                        >
                            <Sparkles className="w-3.5 h-3.5" /> Change plan
                        </button>
                        {currentPlanKey !== "free" && status?.status !== "cancelled" && (
                            <button
                                type="button"
                                onClick={() => setShowCancel(true)}
                                className="h-9 px-3 border border-rose-500 text-rose-700 bg-white text-xs font-mono uppercase tracking-widest hover:bg-rose-50 inline-flex items-center gap-1.5"
                                data-testid="billing-cancel"
                            >
                                <XCircle className="w-3.5 h-3.5" /> Cancel
                            </button>
                        )}
                    </div>
                </div>

                {/* Feature checklist (from /plans) */}
                {currentPlan.features?.length > 0 && (
                    <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-y-1 text-xs">
                        {currentPlan.features.map((f, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* ─── Outstanding snapshot ────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                <div className="border border-border bg-white p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Outstanding</div>
                    <div className="mt-1 font-display font-black text-2xl tabular-nums text-ikb" data-testid="billing-outstanding">
                        {money(outstandingTotal, currency)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{outstanding.length} invoice{outstanding.length === 1 ? "" : "s"}</div>
                </div>
                <div className="border border-border bg-white p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Invoices issued</div>
                    <div className="mt-1 font-display font-black text-2xl tabular-nums text-foreground">
                        {invoices.length}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Lifetime</div>
                </div>
                <div className="border border-border bg-white p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total paid</div>
                    <div className="mt-1 font-display font-black text-2xl tabular-nums text-emerald-700">
                        {money(invoices.filter((i) => i.status === "paid").reduce((a, b) => a + (b.paid_amount || b.total || 0), 0), currency)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Cash received</div>
                </div>
            </div>

            {/* ─── Invoice ledger ──────────────────────────────────── */}
            <div className="border border-border bg-white">
                <div className="px-4 py-2 border-b border-border bg-neutral-50 text-[10px] font-mono uppercase tracking-widest flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-ikb" /> Invoices · {invoices.length}
                </div>
                {loading ? (
                    <div className="p-6 text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
                ) : invoices.length === 0 ? (
                    <div className="p-6 text-sm text-muted-foreground text-center">No invoices yet — you&apos;ll see them here once your subscription is billed.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 border-b border-border">
                            <tr>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Invoice</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Issued</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Due</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Amount</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Status</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="border-b border-border hover:bg-secondary/40" data-testid={`billing-invoice-${inv.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{inv.invoice_number}</div>
                                        <div className="text-[10px] text-muted-foreground">{inv.description}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{inv.issued_at?.slice(0, 10)}</td>
                                    <td className="px-3 py-2 text-xs">{inv.due_at?.slice(0, 10)}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums">{money(inv.total, inv.currency)}</td>
                                    <td className="px-3 py-2">
                                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${STATUS_STYLE[inv.status] || "border-border"}`}>
                                            {inv.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <button type="button" onClick={() => downloadPdf(inv, false)}
                                            className="h-7 px-2 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-50 inline-flex items-center gap-1"
                                            data-testid={`billing-download-${inv.id}`}>
                                            <Download className="w-3 h-3" /> Invoice
                                        </button>
                                        {inv.status === "paid" && (
                                            <button type="button" onClick={() => downloadPdf(inv, true)}
                                                className="ml-1.5 h-7 px-2 border border-emerald-500 text-emerald-700 bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-emerald-50 inline-flex items-center gap-1"
                                                data-testid={`billing-receipt-${inv.id}`}>
                                                <Download className="w-3 h-3" /> Receipt
                                            </button>
                                        )}
                                        {(inv.status === "pending" || inv.status === "overdue") && (
                                            <button type="button" onClick={() => payInvoice(inv)}
                                                className="ml-1.5 h-7 px-2 border border-ikb bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1"
                                                data-testid={`billing-pay-${inv.id}`}>
                                                <CreditCard className="w-3 h-3" /> Pay
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ─── Change plan drawer ──────────────────────────────── */}
            {showChangePlan && (
                <PlanChangeDrawer
                    plans={plans}
                    currentPlanKey={currentPlanKey}
                    changing={changing}
                    onClose={() => setShowChangePlan(false)}
                    onChange={changePlan}
                />
            )}

            {/* ─── Cancel dialog ───────────────────────────────────── */}
            {showCancel && (
                <ConfirmDialog
                    title="Cancel subscription?"
                    body={
                        <div className="space-y-2 text-sm">
                            <p>You'll keep access to the <b className="uppercase font-mono text-ikb">{currentPlan.name}</b> plan until the end of your current billing period ({status?.next_billing_at?.slice(0, 10) || "next cycle"}).</p>
                            <p>After that, your account will drop to <b>Free</b>. You can re-subscribe any time.</p>
                        </div>
                    }
                    confirmLabel="Yes, cancel"
                    danger
                    busy={cancelling}
                    onClose={() => setShowCancel(false)}
                    onConfirm={cancelSubscription}
                />
            )}
        </div>
    );
}

/** PlanChangeDrawer — right-side drawer with the plan matrix + upgrade
 *  or downgrade CTAs. Uses the same /plans catalog as the pricing page. */
function PlanChangeDrawer({ plans, currentPlanKey, changing, onClose, onChange }) {
    const currentRank = ordinalRank(currentPlanKey);
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="plan-change-drawer" onClick={onClose}>
            <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Change plan</div>
                        <h2 className="font-display font-black text-xl">Pick a plan that fits</h2>
                    </div>
                    <button type="button" onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center border border-border hover:bg-neutral-100" data-testid="plan-change-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {plans.map((p) => {
                        const rank = ordinalRank(p.key);
                        const isCurrent = p.key === currentPlanKey;
                        const isUpgrade = rank > currentRank;
                        const isDowngrade = rank < currentRank;
                        return (
                            <div key={p.key} className={`border p-4 relative ${isCurrent ? "border-emerald-500 border-2" : p.popular ? "border-ikb border-2" : "border-border"}`}>
                                {p.popular && !isCurrent && (
                                    <div className="absolute -top-2.5 left-3 bg-ikb text-white text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">
                                        Most popular
                                    </div>
                                )}
                                {isCurrent && (
                                    <div className="absolute -top-2.5 left-3 bg-emerald-600 text-white text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">
                                        Current
                                    </div>
                                )}
                                <div className="font-display font-black text-lg">{p.name}</div>
                                <div className="mt-1">
                                    <span className="font-display font-black text-2xl tabular-nums">₹{p.price_inr?.toLocaleString("en-IN") || 0}</span>
                                    <span className="text-xs text-muted-foreground ml-1">/mo</span>
                                </div>
                                {p.features?.length > 0 && (
                                    <ul className="mt-3 space-y-1 text-xs">
                                        {p.features.slice(0, 5).map((f, i) => (
                                            <li key={i} className="flex items-start gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onChange(p.key)}
                                    disabled={isCurrent || changing === p.key}
                                    className={`mt-4 w-full h-9 text-[10px] font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60 ${
                                        isCurrent ? "border border-emerald-500 text-emerald-700 bg-emerald-50 cursor-default"
                                            : isDowngrade ? "border border-neutral-400 text-neutral-700 bg-white hover:bg-neutral-50"
                                            : "bg-ikb text-white hover:bg-ikb/90"
                                    }`}
                                    data-testid={`plan-change-${p.key}`}
                                >
                                    {changing === p.key
                                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</>
                                        : isCurrent ? <><Package className="w-3.5 h-3.5" /> Current plan</>
                                        : isUpgrade ? <><ArrowUpRight className="w-3.5 h-3.5" /> Upgrade to {p.name}</>
                                        : <><ArrowDownRight className="w-3.5 h-3.5" /> Downgrade to {p.name}</>}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/** ConfirmDialog — small centered modal for destructive confirmations. */
function ConfirmDialog({ title, body, confirmLabel, danger, busy, onClose, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="confirm-dialog">
            <div className="bg-white border border-border max-w-md w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="font-display font-black text-lg mb-2">{title}</div>
                <div className="text-sm text-muted-foreground mb-4">{body}</div>
                <div className="flex items-center gap-2 justify-end">
                    <button type="button" onClick={onClose} disabled={busy} className="h-9 px-3 border border-border bg-white text-xs font-mono uppercase tracking-widest hover:bg-neutral-50" data-testid="confirm-cancel">
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className={`h-9 px-3 border text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-60 ${danger ? "border-rose-500 bg-rose-500 text-white hover:bg-rose-600" : "border-ikb bg-ikb text-white hover:bg-ikb/90"}`}
                        data-testid="confirm-ok"
                    >
                        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
